/**
 * JOINT AI WIZARD MOBILE TEST COMPONENT
 * 
 * This is a mobile-optimized test version of the wizard that maintains
 * 100% functional parity with the production wizard while applying
 * mobile UX improvements for testing purposes.
 * 
 * 🚀 TEST ACCESS: /wizard-mobile-test
 * 📱 COMPARE WITH: /wizard (production)
 * 
 * MOBILE OPTIMIZATIONS APPLIED:
 * - Safe area inset support for modern mobile devices
 * - Responsive typography with fluid scaling
 * - Enhanced touch targets and spacing
 * - Improved viewport and keyboard handling
 * - Better mobile scrolling behavior
 * 
 * PRESERVED ELEMENTS (UNTOUCHED):
 * - Hand joint coordinates (medically calibrated)
 * - Image paths and aspect ratios
 * - Step configuration and validation logic
 * - Media capture component integration
 * - All core wizard functionality
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import PhotoCapture from '../components/PhotoCapture'
import VideoCapture from '../components/VideoCapture'
import { MobileOptimizedInput, MobileOptimizedSelect, MobileButtonGroup } from '../components/SmartFormField'
import NavigationBar from '../components/NavigationBar'
import type { Submission } from '../types'
import { JOINT_DEFINITIONS } from '../data/joints'

// Total number of steps in the wizard
const TOTAL_STEPS = 24

type WizardValue = any
type WizardAnswers = Record<string, any>
type SavedMediaMap = Record<string, string | boolean>
type CreateSubmissionResponse = {
  id?: string
  writeToken?: string
  images_account_hash?: string | null
  error?: string
}
type SubmissionAsset = {
  kind: string
  side?: string
  position?: string
  cf_image_id?: string
  cf_stream_uid?: string
}
type SubmissionHydrationResponse = {
  submission?: { meta?: unknown; step?: number }
  assets?: SubmissionAsset[]
  images_account_hash?: string
}
type AutosavePayload = Partial<Submission> & {
  form_data?: WizardAnswers
  media_data?: SavedMediaMap
  completed_at?: string
}


/**
 * STEP CONFIGURATION - BACK TO ORIGINAL
 * Defines all 22 wizard steps with their types and metadata.
 * This configuration drives the entire wizard flow.
 */
const STEP_CONFIG = {
  // Demographics and pain assessment (Steps 1-10)
  1: { type: 'pain_assessment', title: 'Pain Assessment' },
  2: { type: 'age', title: 'Age' },
  3: { type: 'sex', title: 'Sex' },
  4: { type: 'gender', title: 'Gender' },
  5: { type: 'height', title: 'Your Height' },
  6: { type: 'weight', title: 'Your Weight' },
  7: { type: 'left_hand_pain', title: 'Left Hand Pain' },
  8: { type: 'right_hand_pain', title: 'Right Hand Pain' },
  9: { type: 'left_hand_swelling', title: 'Left Hand Swelling' },
  10: { type: 'right_hand_swelling', title: 'Right Hand Swelling' },
  
  // Photo instructions (Step 11) - Combined with camera permission
  11: { type: 'photo_instructions', title: 'Photo Instructions' },
  
  // Photo capture - 8 different hand positions (Steps 12-19)
  12: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_down_spread' },
  13: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_down_fist' },
  14: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_up_spread' },
  15: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_up_fist' },
  16: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_down_spread' },
  17: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_down_fist' },
  18: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_up_spread' },
  19: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_up_fist' },
  
  // Video capture - 4 hand movement recordings (Steps 20-23)
  20: { type: 'video', title: 'Left Hand Video', hand: 'left', position: 'palm_down_movement' },
  21: { type: 'video', title: 'Left Hand Video', hand: 'left', position: 'palm_up_movement' },
  22: { type: 'video', title: 'Right Hand Video', hand: 'right', position: 'palm_down_movement' },
  23: { type: 'video', title: 'Right Hand Video', hand: 'right', position: 'palm_up_movement' },
  
  // Completion (Step 24)
  24: { type: 'thank_you', title: 'Thank You' }
} as const

/**
 * CREATE SUBMISSION - IDENTICAL TO PRODUCTION
 * Creates a new patient submission record in the database.
 * Called once when the wizard starts to get a unique submission ID.
 * 
 * @returns Promise<string> - The unique submission ID
 */
async function createSubmission(): Promise<{ id: string; writeToken: string | null; imagesAccountHash?: string | null }> {
  // In development mode (localhost), use design-demo mode
  if (window.location.hostname === 'localhost') {
    return { id: 'mobile-test-demo', writeToken: null }
  }

  // Create submission via API
  try {
    const studyId = localStorage.getItem('study_id') || `mobile-test-${Date.now()}`

    const response = await fetch('/api/submissions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        study_id: studyId,
        step: 1
      })
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({} as CreateSubmissionResponse)) as CreateSubmissionResponse
      if (response.status === 409) {
        throw new Error('This Study ID has already been used. Please go back and enter a different one.')
      }
      throw new Error(data.error || `Failed to create submission: ${response.status} ${response.statusText}`)
    }

    const result = await response.json() as CreateSubmissionResponse
    if (!result.id) {
      throw new Error('Submission creation response missing id')
    }
    return { id: result.id, writeToken: result.writeToken || null, imagesAccountHash: result.images_account_hash || null }
  } catch (error) {
    Sentry.captureException(error, { tags: { action: 'submission_creation' } })
    throw error
  }
}

/**
 * AUTOSAVE FUNCTION - IDENTICAL TO PRODUCTION
 * Automatically saves the current wizard progress to the database.
 * Called every time the user advances a step or changes form data.
 * Uses debouncing (1-second delay) to avoid excessive API calls.
 * 
 * @param id - The submission ID to update
 * @param payload - The data to save (form responses, media IDs, progress, etc.)
 */
async function autosave(id: string, payload: AutosavePayload, writeToken: string | null) {
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (writeToken) {
      headers['Authorization'] = `Bearer ${writeToken}`
    }
    const response = await fetch(`/api/submissions/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error(`Autosave failed: ${response.status}`)
    }
  } catch (error) {
    Sentry.captureException(error, { tags: { action: 'autosave' } })
  }
}

/**
 * MOBILE OPTIMIZED WIZARD COMPONENT
 * Handles the complete 22-step patient data collection flow with mobile UX improvements
 */
export default function WizardMobileTest() {
  // === STATE MANAGEMENT ===
  const [submissionId, setSubmissionId] = useState<string | null>(null)
  const [writeToken, setWriteToken] = useState<string | null>(null)
  const [step, setStep] = useState(1)
  const [answers, setAnswers] = useState<WizardAnswers>({})
  const [mediaSaved, setMediaSaved] = useState<SavedMediaMap>({})
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imagesAccountHash, setImagesAccountHash] = useState<string>('')
  
  // Developer mode detection
  const isDeveloperMode = new URLSearchParams(window.location.search).get('dev') === '1'
  
  const [showDevPanel, setShowDevPanel] = useState(false)
  
  // Get study ID from localStorage (set on homepage)
  const studyId = localStorage.getItem('study_id')

  // Pre-populate answers for developer testing - IDENTICAL TO PRODUCTION
  useEffect(() => {
    if (isDeveloperMode) {
      setAnswers({
        overall_pain: 6,
        age: 45,
        height_unit: 'ft',
        height_feet: 5,
        height_inches: 8,
        weight_unit: 'lb',
        weight_value: 150,
        left_hand_pain: ['joint_14', 'joint_11'],
        right_hand_pain: ['joint_7'],
        left_hand_swelling: [],
        right_hand_swelling: ['joint_6']
      })
    }
  }, [isDeveloperMode])

  // === BACKEND INITIALIZATION ===
  useEffect(() => {
    const initSubmission = async () => {
      try {
        const existingId = localStorage.getItem('mobile_test_submission_id')
        const existingToken = localStorage.getItem('write_token')
        const currentStudyId = localStorage.getItem('study_id')
        const lastStudyId = localStorage.getItem('last_study_id')

        // Resume existing submission if same study_id
        if (existingId && existingId !== 'undefined' && existingId !== 'null' &&
            currentStudyId === lastStudyId) {
          setSubmissionId(existingId)
          setWriteToken(existingToken)

          // Hydrate progress from server
          if (existingToken && window.location.hostname !== 'localhost') {
            try {
              const response = await fetch(`/api/submissions/${existingId}`, {
                headers: { 'Authorization': `Bearer ${existingToken}` }
              })
              if (response.ok) {
                const data = await response.json() as SubmissionHydrationResponse
                if (data.submission?.meta) {
                  const meta = typeof data.submission.meta === 'string'
                    ? JSON.parse(data.submission.meta) as WizardAnswers : data.submission.meta as WizardAnswers
                  setAnswers(meta)
                }
                if (data.submission?.step) {
                  setStep(data.submission.step)
                }
                if (data.images_account_hash) {
                  setImagesAccountHash(data.images_account_hash)
                }
                if (data.assets) {
                  const saved: SavedMediaMap = {}
                  for (const asset of data.assets) {
                    saved[`${asset.kind}_${asset.side}_${asset.position}`] = asset.cf_image_id || asset.cf_stream_uid || true
                  }
                  setMediaSaved(saved)
                }
              }
            } catch (e) {
              Sentry.captureException(e, { tags: { action: 'hydration' } })
            }
          }

          setError(null)
          return
        } else if (currentStudyId !== lastStudyId) {
          localStorage.removeItem('mobile_test_submission_id')
          localStorage.removeItem('write_token')
        }

        // Create new submission
        const result = await createSubmission()
        if (result.id && result.id !== 'undefined') {
          localStorage.setItem('mobile_test_submission_id', result.id)
          localStorage.setItem('last_study_id', currentStudyId || '')
          if (result.writeToken) {
            localStorage.setItem('write_token', result.writeToken)
          }
          setSubmissionId(result.id)
          setWriteToken(result.writeToken)
          if (result.imagesAccountHash) {
            setImagesAccountHash(result.imagesAccountHash)
          }
        }
        setError(null)
      } catch (error) {
        Sentry.captureException(error, { tags: { action: 'init_submission' } })
        setError(error instanceof Error ? error.message : 'Failed to initialize submission')
      }
    }

    initSubmission()
  }, [])

  // === AUTOSAVE FUNCTIONALITY ===
  useEffect(() => {
    if (!submissionId) return

    const saveData = async () => {
      try {
        const payload = {
          step: step,
          form_data: answers,
          media_data: mediaSaved,
          updated_at: new Date().toISOString()
        }
        await autosave(submissionId, payload, writeToken)
      } catch (error) {
        Sentry.captureException(error, { tags: { action: 'savedata' } })
      }
    }

    const timeoutId = setTimeout(saveData, 1000)
    return () => clearTimeout(timeoutId)
  }, [submissionId, writeToken, step, answers, mediaSaved])

  // === SCROLL TO TOP ON STEP CHANGES - MOBILE UX IMPROVEMENT ===
  // Automatically scroll to top when step changes to ensure users see step titles and instructions
  useEffect(() => {
    // Small delay to ensure DOM updates complete before scrolling
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 100)
    
    return () => clearTimeout(timeoutId)
  }, [step])

  // === COMPLETION HANDLER ===
  useEffect(() => {
    if (step === TOTAL_STEPS && submissionId) {
      const completeSubmission = async () => {
        try {
          await autosave(submissionId, {
            step: TOTAL_STEPS,
            status: 'submitted',
            form_data: answers,
            media_data: mediaSaved,
            completed_at: new Date().toISOString()
          }, writeToken)
        } catch (error) {
          Sentry.captureException(error, { tags: { action: 'complete_submission' } })
        } finally {
          localStorage.removeItem('mobile_test_submission_id')
          localStorage.removeItem('study_id')
          localStorage.removeItem('last_study_id')
          localStorage.removeItem('write_token')
        }
      }
      completeSubmission()
    }
  }, [step, submissionId, writeToken, answers, mediaSaved])

  // === COMPUTED VALUES - IDENTICAL TO PRODUCTION ===
  const progress = Math.round((step / TOTAL_STEPS) * 100) // Progress percentage for progress bar
  const currentStepConfig = STEP_CONFIG[step as keyof typeof STEP_CONFIG] // Current step metadata
  
  // === NAVIGATION FUNCTIONS ===
  const next = () => setStep(s => Math.min(TOTAL_STEPS, s + 1))
  const prev = () => setStep(s => Math.max(1, s - 1))
  const update = (k: string, v: WizardValue) => {
    setAnswers(a => ({ ...a, [k]: v }))
  }

  // === VALIDATION LOGIC - IDENTICAL TO PRODUCTION ===
  // Determines if user can proceed to next step based on current step requirements
  const canProceed = (() => {
    if (!currentStepConfig) return false
    
    const stepType = currentStepConfig.type
    
    switch (stepType) {
      case 'pain_assessment':
        // Always allow proceeding - default value 5 is valid, any slider movement is valid
        return true
      case 'age':
        return answers.age !== undefined && answers.age >= 10 && answers.age <= 99
      case 'sex':
        return answers.sex !== undefined
      case 'gender':
        return answers.gender !== undefined
      case 'height':
        const heightUnit = answers.height_unit || 'ft'
        if (heightUnit === 'ft') {
          return answers.height_feet !== undefined && answers.height_feet !== '' && 
                 answers.height_inches !== undefined && answers.height_inches !== ''
        } else {
          const heightCm = parseInt(answers.height_cm)
          return answers.height_cm !== undefined && answers.height_cm !== '' && 
                 heightCm >= 50 && heightCm <= 250
        }
      case 'weight':
        const weightValue = parseInt(answers.weight_value)
        const weightUnit = answers.weight_unit
        if (!answers.weight_value || !weightUnit || isNaN(weightValue)) return false
        
        // Validate weight ranges
        if (weightUnit === 'lb') {
          return weightValue >= 30 && weightValue <= 500
        } else {
          return weightValue >= 20 && weightValue <= 200
        }
      case 'left_hand_pain':
      case 'right_hand_pain':  
      case 'left_hand_swelling':
      case 'right_hand_swelling':
        // Always allow Next button - selecting joints is optional
        // Patients may have no pain or swelling, which is valid data
        return true
      case 'photo_instructions':
        return true
      case 'photo':
      case 'video':
        const mediaKey = `${stepType}_${currentStepConfig.hand}_${currentStepConfig.position}`
        return mediaSaved[mediaKey] !== undefined
      case 'thank_you':
        return true
      default:
        return false
    }
  })()

  if (error) {
    return (
      <div className='min-h-screen p-4'>
        <div className='max-w-md mx-auto'>
          <div className='card p-5 space-y-4'>
            <div className='text-center space-y-3'>
              <h2 className='text-xl font-bold text-red-400'>Mobile Test Setup Error</h2>
              <p className='text-neutral-300'>{error}</p>
              <button 
                className='btn w-full' 
                onClick={() => {
                  setError(null)
                  localStorage.removeItem('mobile_test_submission_id')
                  localStorage.removeItem('last_study_id')
                  window.location.reload()
                }}
              >
                Try Again
              </button>
              <Link to='/' className='text-blue-400 hover:underline block'>← Back to Home</Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="bg-white min-h-screen w-full mx-auto relative flex flex-col"
      style={{
        maxWidth: 'clamp(320px, 100vw, 420px)',
        minHeight: '100dvh'
      }}
    >
      {/* Developer Panel - Only shows when ?dev=1 */}
      {isDeveloperMode && (
        <>
          {/* Developer Toggle Button - MOBILE OPTIMIZATION: Better positioning */}
          <button
            onClick={() => setShowDevPanel(!showDevPanel)}
            className="fixed top-2 right-2 z-50 bg-yellow-400 text-black px-3 py-2 rounded-full text-xs font-bold shadow-lg hover:bg-yellow-300"
            style={{
              // MOBILE OPTIMIZATION: Respect safe area
              top: 'max(0.5rem, env(safe-area-inset-top))',
              right: 'max(0.5rem, env(safe-area-inset-right))'
            }}
          >
            🔧 MOBILE TEST {showDevPanel ? '×' : '☰'}
          </button>

          {/* Developer Navigation Overlay */}
          {showDevPanel && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
                <div className="p-4 border-b border-gray-200 sticky top-0 bg-white">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-900">🧪 Mobile Test Navigation</h3>
                    <button 
                      onClick={() => setShowDevPanel(false)}
                      className="text-gray-500 hover:text-gray-700 text-xl"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Current Step: {step} - {STEP_CONFIG[step as keyof typeof STEP_CONFIG]?.title}</p>
                  <p className="text-xs text-blue-600 mt-1">Compare with: /wizard (production)</p>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Quick Jump Buttons */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {Object.entries(STEP_CONFIG).map(([stepNum, config]) => {
                      const num = parseInt(stepNum)
                      const isPhoto = config.type === 'photo'
                      const isVideo = config.type === 'video'
                      const isPain = config.type.includes('pain') || config.type.includes('swelling')
                      
                      return (
                        <button
                          key={stepNum}
                          onClick={() => {
                            setStep(num)
                            setShowDevPanel(false)
                          }}
                          className={`p-2 rounded text-xs font-medium ${
                            step === num 
                              ? 'bg-blue-600 text-white' 
                              : isPhoto
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : isVideo
                                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                  : isPain
                                    ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {num}. {isPhoto ? '📸' : isVideo ? '🎥' : isPain ? '👋' : '📝'}
                        </button>
                      )
                    })}
                  </div>

                  {/* Mobile Test Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <h4 className="font-semibold text-blue-900">Mobile Test Mode:</h4>
                    <p className="text-sm text-blue-800">Type: {currentStepConfig?.type}</p>
                    <p className="text-sm text-blue-800">Optimizations: Safe area, responsive text, touch targets</p>
                  </div>

                  {/* Quick Test Areas */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">Test Problem Areas:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => { setStep(1); setShowDevPanel(false) }} className="p-2 bg-orange-100 text-orange-800 rounded text-xs hover:bg-orange-200">📱 Pain Slider (1)</button>
                      <button onClick={() => { setStep(5); setShowDevPanel(false) }} className="p-2 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200">👋 Hand Joints (5-8)</button>
                      <button onClick={() => { setStep(10); setShowDevPanel(false) }} className="p-2 bg-green-100 text-green-800 rounded text-xs hover:bg-green-200">📸 Photo Steps (10+)</button>
                      <button onClick={() => { setStep(22); setShowDevPanel(false) }} className="p-2 bg-purple-100 text-purple-800 rounded text-xs hover:bg-purple-200">🏁 Final Nav Test</button>
                    </div>
                  </div>

                  {/* Developer Actions */}
                  <div className="border-t border-gray-200 pt-4 space-y-2">
                    <h4 className="font-semibold text-gray-900">Test Actions:</h4>
                    <button 
                      onClick={() => {
                        localStorage.clear()
                        window.location.reload()
                      }}
                      className="w-full p-2 bg-red-100 text-red-800 rounded text-sm hover:bg-red-200"
                    >
                      🗑️ Clear Test Data & Restart
                    </button>
                    <button 
                      onClick={() => window.open('/wizard', '_blank')}
                      className="w-full p-2 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
                    >
                      🔗 Open Production Version
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Fixed Header - MOBILE OPTIMIZATION: Clean positioning */}
      <div 
        className="fixed inset-x-0 top-0 z-10 mx-auto"
        style={{
          maxWidth: 'clamp(320px, 100vw, 420px)',
          // MOBILE OPTIMIZATION: Proper safe area handling
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        {/* White header with logo */}
        <div className="bg-white h-[65px] w-full border-b border-border-light relative">
          <div className="flex items-center justify-center h-full">
            <div className="w-[160px] h-10">
              <div
                className="bg-center bg-cover bg-no-repeat size-full"
                style={{ backgroundImage: `url('/images/logo.png')` }}
              />
            </div>
          </div>
        </div>
        
        {/* Gray progress bar section */}
        <div className="bg-surface-muted h-1.5 w-full relative rounded-full">
          <div className="bg-brand-green h-1.5 left-0 top-0 absolute transition-all duration-300 rounded-full" style={{
            width: `${progress}%`
          }} />
        </div>
      </div>

      {/* Scrollable Content Area - MOBILE OPTIMIZATION: Clean spacing and proper scrolling */}
      <div 
        className="flex-1 overflow-y-auto"
        style={{
          paddingTop: `calc(71px + env(safe-area-inset-top))`, // 65px header + 6px progress bar
          paddingBottom: `calc(var(--nav-height) + env(safe-area-inset-bottom) + 16px)`,
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          scrollPaddingBottom: `calc(var(--nav-height) + env(safe-area-inset-bottom))`
        }}
      >
        {(() => {
          if (!currentStepConfig) return <div>Invalid step</div>
          
          const stepType = currentStepConfig.type
          
          switch (stepType) {
            case 'pain_assessment':
              const painValue = answers.pain_score ?? 5
              
              // Set default pain score if not already set
              if (answers.pain_score === undefined) {
                update('pain_score', 5)
              }
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 pt-6 pb-8 min-h-full flex flex-col justify-start">
                  {/* Question text - MOBILE OPTIMIZATION: Responsive typography */}
                  <div className="text-center mb-6 sm:mb-8">
                    <h2
                      className="font-bold text-black leading-tight text-fluid-lg"
                    >
                      Considering all of the ways your disease affects you, how well are you doing in the past week?
                    </h2>
                  </div>
                  
                  {/* Simple, clean slider - MOBILE OPTIMIZATION: Better spacing */}
                  <div className="w-full py-6">
                    {/* Simple instruction with proper spacing */}
                    <div className="text-center mb-12 sm:mb-16">
                      <p
                        className="text-gray-600 text-fluid-sm"
                      >
                        Tap and drag to select your number:
                      </p>
                    </div>
                    
                    <div className="relative">
                      {/* Interactive value bubble - MOBILE OPTIMIZATION: Larger touch target */}
                      <div 
                        className="absolute flex flex-col items-center transition-all duration-200 cursor-grab active:cursor-grabbing z-20"
                        style={{ 
                          left: `calc(${(painValue - 1) * 11.11}%)`,
                          top: '-48px',
                          transform: 'translateX(-50%)'
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const handleMouseMove = (moveEvent: MouseEvent) => {
                            const track = document.querySelector('.slider-track') as HTMLElement;
                            if (!track) return;
                            const rect = track.getBoundingClientRect();
                            const x = moveEvent.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(1, x / rect.width));
                            const newValue = Math.round(percentage * 9 + 1);
                            update('pain_score', Math.min(10, Math.max(1, newValue)));
                          };
                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          const handleTouchMove = (moveEvent: TouchEvent) => {
                            const track = document.querySelector('.slider-track') as HTMLElement;
                            if (!track) return;
                            const rect = track.getBoundingClientRect();
                            const x = moveEvent.touches[0].clientX - rect.left;
                            const percentage = Math.max(0, Math.min(1, x / rect.width));
                            const newValue = Math.round(percentage * 9 + 1);
                            update('pain_score', Math.min(10, Math.max(1, newValue)));
                          };
                          const handleTouchEnd = () => {
                            document.removeEventListener('touchmove', handleTouchMove);
                            document.removeEventListener('touchend', handleTouchEnd);
                          };
                          document.addEventListener('touchmove', handleTouchMove);
                          document.addEventListener('touchend', handleTouchEnd);
                        }}
                      >
                        <div 
                          className="bg-gray-900 text-white px-3 py-1.5 rounded-lg font-semibold shadow-lg text-center hover:bg-gray-800 transition-colors text-fluid-base"
                          style={{
                            // MOBILE OPTIMIZATION: Better touch target size
                            minWidth: '48px',
                            minHeight: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {painValue}
                        </div>
                        <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-gray-900" style={{ marginTop: '0px' }}></div>
                      </div>
                      
                      {/* Simple gradient track - MOBILE OPTIMIZATION: Larger touch area */}
                      <div className="slider-track w-full h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-full relative shadow-sm">
                        <input 
                          type="range"
                          min="1"
                          max="10"
                          value={painValue}
                          onChange={(e) => update('pain_score', Number(e.target.value))}
                          className="w-full opacity-0 cursor-pointer absolute z-10"
                          style={{
                            // MOBILE OPTIMIZATION: Larger touch area
                            height: '48px',
                            top: '-18px'
                          }}
                        />
                        
                        {/* Simple handle with good size for touch - MOBILE OPTIMIZATION: Larger handle */}
                        <div 
                          className="absolute bg-brand-blue rounded-full shadow-md transition-all duration-200 border-4 border-white pointer-events-none"
                          style={{ 
                            left: `calc(${(painValue - 1) * 11.11}%)`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            // MOBILE OPTIMIZATION: Larger touch handle
                            width: '32px',
                            height: '32px'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Range meanings with golden ratio spacing - MOBILE OPTIMIZATION: Better responsive layout */}
                  <div 
                    className="flex justify-center space-x-4 sm:space-x-8 text-center"
                    style={{ marginBottom: '34px' }}
                  >
                    <div className="flex items-center">
                      <div className="w-3.5 h-3.5 bg-green-400 rounded-full mr-2"></div>
                      <span
                        className="font-medium text-gray-700 text-fluid-xs"
                      >
                        1-3: Best
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3.5 h-3.5 bg-yellow-400 rounded-full mr-2"></div>
                      <span
                        className="font-medium text-gray-700 text-fluid-xs"
                      >
                        4-6: Moderate
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3.5 h-3.5 bg-red-400 rounded-full mr-2"></div>
                      <span
                        className="font-medium text-gray-700 text-fluid-xs"
                      >
                        7-10: Worst
                      </span>
                    </div>
                  </div>
                </div>
              )
            
            case 'age':
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-4">
                  {/* Clean age input title - MOBILE OPTIMIZATION: Responsive typography */}
                  <div className="text-center mb-4">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      What is your age?
                    </h2>
                  </div>
                  
                  {/* Simplified input field for keyboard optimization - no auto-scroll */}
                  <div className="flex justify-center">
                    <MobileOptimizedInput
                      type="number"
                      value={answers.age || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        update('age', value ? parseInt(value) : undefined);
                      }}
                      onEnterPress={() => {
                        if (canProceed) next()
                      }}
                      placeholder=""
                      className="h-fluid-btn"
                      style={{
                        width: 'clamp(120px, 30vw, 140px)',
                        fontSize: 'clamp(24px, 7vw, 28px)'
                      }}
                      min="10"
                      max="99"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      helperText="years old"
                    />
                  </div>
                </div>
              )

            case 'sex':
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 pt-6 pb-8 min-h-full flex flex-col justify-start">
                  {/* Sex question title */}
                  <div className="text-center mb-6 sm:mb-8">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      What is your sex?
                    </h2>
                  </div>
                  
                  {/* Sex selection buttons */}
                  <MobileButtonGroup className="flex-col space-y-4">
                    {['Female', 'Male', 'Prefer not to say'].map((option) => (
                      <button
                        key={option}
                        onClick={() => update('sex', option)}
                        className={`w-full h-fluid-btn text-fluid-base ${
                          answers.sex === option ? 'option-btn-selected' : 'option-btn'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </MobileButtonGroup>
                </div>
              )

            case 'gender':
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  {/* Gender question title */}
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      What is your gender?
                    </h2>
                  </div>
                  
                  {/* Gender selection buttons */}
                  <MobileButtonGroup className="flex-col space-y-4">
                    {['Man', 'Woman', 'Other', 'Non-binary', 'Prefer not to say'].map((option) => (
                      <button
                        key={option}
                        onClick={() => update('gender', option)}
                        className={`w-full h-fluid-btn text-fluid-base ${
                          answers.gender === option ? 'option-btn-selected' : 'option-btn'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </MobileButtonGroup>
                </div>
              )

            case 'height':
              const heightUnit = answers.height_unit
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  {/* Direct question - MOBILE OPTIMIZATION: Responsive typography */}
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black leading-tight text-fluid-xl"
                    >
                      Do you know your height in feet & inches or centimeters?
                    </h2>
                  </div>
                  
                  {/* Unit selection buttons */}
                  <MobileButtonGroup className="flex-col space-y-4 mb-8">
                    <button
                      onClick={() => update('height_unit', 'ft')}
                      className={`w-full h-fluid-btn text-fluid-base ${
                        heightUnit === 'ft' ? 'option-btn-selected' : 'option-btn'
                      }`}
                    >
                      Feet & Inches
                    </button>

                    <button
                      onClick={() => update('height_unit', 'cm')}
                      className={`w-full h-fluid-btn text-fluid-base ${
                        heightUnit === 'cm' ? 'option-btn-selected' : 'option-btn'
                      }`}
                    >
                      Centimeters
                    </button>
                  </MobileButtonGroup>
                  
                  {/* Input fields based on selection */}
                  {heightUnit === 'ft' && (
                    <div className="space-y-6">
                      {/* Clear instruction text - MOBILE OPTIMIZATION: Better spacing */}
                      <div className="text-center mb-4 sm:mb-6">
                        <p
                          className="font-medium text-gray-600 text-fluid-base"
                        >
                          Please select your height in feet and inches:
                        </p>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <MobileOptimizedSelect
                            value={answers.height_feet || ''}
                            onChange={(e) => update('height_feet', e.target.value)}
                            onEnterPress={() => {
                              if (canProceed) next()
                            }}
                            label="Feet"
                            className="h-fluid-btn text-fluid-md"
                          >
                            <option value="">-</option>
                            {[3,4,5,6,7,8].map(ft => (
                              <option key={ft} value={ft}>{ft}</option>
                            ))}
                          </MobileOptimizedSelect>
                        </div>
                        <div className="flex-1">
                          <MobileOptimizedSelect
                            value={answers.height_inches || ''}
                            onChange={(e) => update('height_inches', e.target.value)}
                            onEnterPress={() => {
                              if (canProceed) next()
                            }}
                            label="Inches"
                            className="h-fluid-btn text-fluid-md"
                          >
                            <option value="">-</option>
                            {Array.from({length: 12}, (_, i) => (
                              <option key={i} value={i}>{i}</option>
                            ))}
                          </MobileOptimizedSelect>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {heightUnit === 'cm' && (
                    <div className="space-y-6" data-conditional-input>
                      {/* Clear instruction text - MOBILE OPTIMIZATION: Better spacing */}
                      <div className="text-center mb-4 sm:mb-6">
                        <p
                          className="font-medium text-gray-600 text-fluid-base"
                        >
                          Please enter your height in centimeters:
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <MobileOptimizedInput
                          type="number"
                          value={answers.height_cm || ''}
                          onChange={(e) => {
                            const value = e.target.value
                            update('height_cm', value)
                          }}
                          onEnterPress={() => {
                            if (canProceed) next()
                          }}
                          placeholder=""
                          min="100"
                          max="250"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          helperText="centimeters"
                          className="h-fluid-btn text-fluid-xl"
                        />
                        
                      </div>
                    </div>
                  )}
                </div>
              )
              
            case 'weight':
              const weightUnit = answers.weight_unit
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  {/* Direct question - MOBILE OPTIMIZATION: Responsive typography */}
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black leading-tight text-fluid-xl"
                    >
                      Do you know your weight in pounds or kilograms?
                    </h2>
                  </div>
                  
                  {/* Unit selection buttons */}
                  <MobileButtonGroup className="flex-col space-y-4 mb-8">
                    <button
                      onClick={() => update('weight_unit', 'lb')}
                      className={`w-full h-fluid-btn text-fluid-base ${
                        weightUnit === 'lb' ? 'option-btn-selected' : 'option-btn'
                      }`}
                    >
                      Pounds (lbs)
                    </button>

                    <button
                      onClick={() => update('weight_unit', 'kg')}
                      className={`w-full h-fluid-btn text-fluid-base ${
                        weightUnit === 'kg' ? 'option-btn-selected' : 'option-btn'
                      }`}
                    >
                      Kilograms (kg)
                    </button>
                  </MobileButtonGroup>
                  
                  {/* Input field based on selection */}
                  {weightUnit && (
                    <div className="space-y-6" data-conditional-input>
                      {/* Clear instruction text - MOBILE OPTIMIZATION: Better spacing */}
                      <div className="text-center mb-4 sm:mb-6">
                        <p
                          className="font-medium text-gray-600 text-fluid-base"
                        >
                          Please enter your weight in {weightUnit === 'lb' ? 'pounds' : 'kilograms'}:
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <MobileOptimizedInput
                          type="number"
                          value={answers.weight_value || ''}
                          onChange={(e) => {
                            const value = e.target.value
                            update('weight_value', value)
                          }}
                          onEnterPress={() => {
                            if (canProceed) next()
                          }}
                          placeholder=""
                          min={weightUnit === 'lb' ? '50' : '20'}
                          max={weightUnit === 'lb' ? '500' : '200'}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          helperText={weightUnit === 'lb' ? 'pounds' : 'kilograms'}
                          className="h-fluid-btn text-fluid-xl"
                        />
                        
                      </div>
                    </div>
                  )}
                </div>
              )

            case 'left_hand_pain':
              const leftHandPain = answers.left_hand_pain || []
              
              const toggleJoint = (jointId: string) => {
                const newSelection = leftHandPain.includes(jointId)
                  ? leftHandPain.filter((j: string) => j !== jointId)
                  : [...leftHandPain, jointId]
                update('left_hand_pain', newSelection)
              }
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  {/* Consistent title structure - MOBILE OPTIMIZATION: Responsive typography */}
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      Left Hand Pain
                    </h2>
                  </div>
                  
                  {/* Instruction text - MOBILE OPTIMIZATION: Better responsive text */}
                  <div className="text-center mb-6">
                    <p
                      className="font-bold text-black leading-fluid-lg max-w-[320px] mx-auto text-fluid-lg"
                    >
                      Tap the joints that are painful, achy, or stiff:
                    </p>
                  </div>

                  {/* Legend - MOBILE OPTIMIZATION: Moved above hand for better visibility */}
                  <div className="flex items-center justify-center space-x-8 mb-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-[19px] h-[19px] rounded-full bg-[rgba(255,0,0,0.35)] border-[3px] border-[#ff0000]"></div>
                      <span
                        className="text-black font-medium text-fluid-sm"
                      >
                        Pain
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-[19px] h-[19px] rounded-full bg-white border-[3px] border-[#6b7280]"></div>
                      <span
                        className="text-black font-medium text-fluid-sm"
                      >
                        No Pain
                      </span>
                    </div>
                  </div>

                  {/* Hand with joints - responsive container - PRESERVED ASPECT RATIO AND COORDINATES */}
                  <div className="flex justify-center mb-8">
                    <div className="relative w-full max-w-[320px] aspect-[328/379]">
                      {/* Hand background - PRESERVED IMAGE PATH */}
                      <div className="absolute inset-0 bg-gray-50 rounded-lg"
                           style={{ backgroundImage: `url('/images/hand.png')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center' }}>
                      </div>
                      
                      {/* 
                        JOINT POSITIONING - PRESERVED COORDINATES FROM PRODUCTION
                        - Hand container is 328px wide × 379px tall in Figma
                        - Coordinates are percentages: x = (figma_left - 33) / 328 * 100, y = (figma_top - 288) / 379 * 100
                        - THESE COORDINATES ARE MEDICALLY CALIBRATED AND PRESERVED EXACTLY
                      */}
                      {JOINT_DEFINITIONS.left.map(joint => (
                        <button
                          key={joint.id}
                          className={`absolute rounded-full border-[3px] transition-all duration-300 transform -translate-x-1/2 -translate-y-1/2 active:scale-95 ${ 
                            leftHandPain.includes(joint.id) 
                              ? 'bg-[#ef4444] border-[#dc2626] shadow-lg' 
                              : 'bg-white border-[#6b7280] hover:border-[#374151] hover:shadow-md'
                          }`}
                          style={{ 
                            left: `${joint.x}%`, 
                            top: `${joint.y}%`,
                            // MOBILE OPTIMIZATION: Slightly larger touch targets while preserving positioning
                            width: 'clamp(36px, 9vw, 40px)',
                            height: 'clamp(36px, 9vw, 40px)'
                          }}
                          onClick={() => toggleJoint(joint.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )

            case 'right_hand_pain':
              const rightHandPain = answers.right_hand_pain || []
              
              const toggleRightJoint = (jointId: string) => {
                const newSelection = rightHandPain.includes(jointId)
                  ? rightHandPain.filter((j: string) => j !== jointId)
                  : [...rightHandPain, jointId]
                update('right_hand_pain', newSelection)
              }
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  {/* Consistent title structure - MOBILE OPTIMIZATION: Responsive typography */}
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      Right Hand Pain
                    </h2>
                  </div>
                  
                  {/* Instruction text - MOBILE OPTIMIZATION: Better responsive text */}
                  <div className="text-center mb-6">
                    <p
                      className="font-bold text-black leading-fluid-lg max-w-[320px] mx-auto text-fluid-lg"
                    >
                      Tap the joints that are painful, achy, or stiff:
                    </p>
                  </div>

                  {/* Legend - MOBILE OPTIMIZATION: Moved above hand for better visibility */}
                  <div className="flex items-center justify-center space-x-8 mb-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-[19px] h-[19px] rounded-full bg-[rgba(255,0,0,0.35)] border-[3px] border-[#ff0000]"></div>
                      <span
                        className="text-black font-medium text-fluid-sm"
                      >
                        Pain
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-[19px] h-[19px] rounded-full bg-white border-[3px] border-[#6b7280]"></div>
                      <span
                        className="text-black font-medium text-fluid-sm"
                      >
                        No Pain
                      </span>
                    </div>
                  </div>

                  {/* Hand with joints - responsive container - PRESERVED ASPECT RATIO AND COORDINATES */}
                  <div className="flex justify-center mb-8">
                    <div className="relative w-full max-w-[320px] aspect-[328/379]">
                      {/* Hand background - mirrored for right hand - PRESERVED IMAGE PATH AND TRANSFORM */}
                      <div className="absolute inset-0 bg-gray-50 rounded-lg" 
                           style={{ backgroundImage: `url('/images/hand.png')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    transform: 'scaleX(-1)' }}>
                      </div>
                      
                      {/* 
                        RIGHT HAND JOINT COORDINATES - PRESERVED FROM PRODUCTION
                        - Coordinates are mirrored: x = 100 - left_hand_x, y stays same  
                        - THESE COORDINATES ARE MEDICALLY CALIBRATED AND PRESERVED EXACTLY
                      */}
                      {JOINT_DEFINITIONS.right.map(joint => (
                        <button
                          key={joint.id}
                          className={`absolute rounded-full border-[3px] transition-all duration-300 transform -translate-x-1/2 -translate-y-1/2 active:scale-95 ${ 
                            rightHandPain.includes(joint.id) 
                              ? 'bg-[#ef4444] border-[#dc2626] shadow-lg' 
                              : 'bg-white border-[#6b7280] hover:border-[#374151] hover:shadow-md'
                          }`}
                          style={{ 
                            left: `${joint.x}%`, 
                            top: `${joint.y}%`,
                            // MOBILE OPTIMIZATION: Slightly larger touch targets while preserving positioning
                            width: 'clamp(36px, 9vw, 40px)',
                            height: 'clamp(36px, 9vw, 40px)'
                          }}
                          onClick={() => toggleRightJoint(joint.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )

            case 'left_hand_swelling':
              const leftHandSwelling = answers.left_hand_swelling || []
              
              const toggleLeftSwelling = (jointId: string) => {
                const newSelection = leftHandSwelling.includes(jointId)
                  ? leftHandSwelling.filter((j: string) => j !== jointId)
                  : [...leftHandSwelling, jointId]
                update('left_hand_swelling', newSelection)
              }
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      Left Hand Swelling
                    </h2>
                    <p
                      className="font-bold text-black leading-fluid-lg mt-4 text-fluid-lg"
                    >
                      Tap the joints that you feel are swollen:
                    </p>
                  </div>

                  {/* Legend - MOBILE OPTIMIZATION: Moved above hand for better visibility */}
                  <div className="flex items-center justify-center gap-6 font-bold mb-6 text-fluid-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[rgba(239,68,68,0.4)] border-2 border-[#ef4444]"></div>
                      <span className="text-black">Swollen</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-white border-2 border-gray-400"></div>
                      <span className="text-black">Not Swollen</span>
                    </div>
                  </div>

                  {/* Hand with joints - PRESERVED COORDINATES AND ASPECT RATIO */}
                  <div className="relative w-full max-w-[320px] aspect-[328/379] mb-8 mx-auto">
                    {/* Hand background - PRESERVED IMAGE PATH */}
                    <div className="absolute inset-0 bg-gray-50 rounded-lg"
                         style={{ backgroundImage: `url('/images/hand.png')`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center' }}>
                    </div>
                    
                    {/* LEFT HAND SWELLING COORDINATES - PRESERVED FROM PRODUCTION */}
                    {JOINT_DEFINITIONS.left.map(joint => (
                      <button
                        key={joint.id}
                        className={`absolute rounded-full border-[3px] transition-all duration-300 transform -translate-x-1/2 -translate-y-1/2 active:scale-95 ${ 
                          leftHandSwelling.includes(joint.id) 
                            ? 'bg-[#ef4444] border-[#dc2626] shadow-lg' 
                            : 'bg-white border-[#6b7280] hover:border-[#374151] hover:shadow-md'
                        }`}
                        style={{ 
                          left: `${joint.x}%`, 
                          top: `${joint.y}%`,
                          // MOBILE OPTIMIZATION: Slightly larger touch targets while preserving positioning
                          width: 'clamp(36px, 9vw, 40px)',
                          height: 'clamp(36px, 9vw, 40px)'
                        }}
                        onClick={() => toggleLeftSwelling(joint.id)}
                      />
                    ))}
                  </div>
                </div>
              )

            case 'right_hand_swelling':
              const rightHandSwelling = answers.right_hand_swelling || []
              
              const toggleRightSwelling = (jointId: string) => {
                const newSelection = rightHandSwelling.includes(jointId)
                  ? rightHandSwelling.filter((j: string) => j !== jointId)
                  : [...rightHandSwelling, jointId]
                update('right_hand_swelling', newSelection)
              }
              
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      Right Hand Swelling
                    </h2>
                    <p
                      className="font-bold text-black leading-fluid-lg mt-4 text-fluid-lg"
                    >
                      Tap the joints that you feel are swollen:
                    </p>
                  </div>

                  {/* Legend - MOBILE OPTIMIZATION: Moved above hand for better visibility */}
                  <div className="flex items-center justify-center gap-6 font-bold mb-6 text-fluid-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[rgba(239,68,68,0.4)] border-2 border-[#ef4444]"></div>
                      <span className="text-black">Swollen</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-white border-2 border-gray-400"></div>
                      <span className="text-black">Not Swollen</span>
                    </div>
                  </div>

                  {/* Hand with joints - PRESERVED COORDINATES AND ASPECT RATIO */}
                  <div className="relative w-full max-w-[320px] aspect-[328/379] mb-8 mx-auto">
                    {/* Hand background - mirrored for right hand - PRESERVED IMAGE PATH AND TRANSFORM */}
                    <div className="absolute inset-0 bg-gray-50 rounded-lg" 
                         style={{ backgroundImage: `url('/images/hand.png')`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  transform: 'scaleX(-1)' }}>
                    </div>
                    
                    {/* RIGHT HAND SWELLING COORDINATES - PRESERVED FROM PRODUCTION */}
                    {JOINT_DEFINITIONS.right.map(joint => (
                      <button
                        key={joint.id}
                        className={`absolute rounded-full border-[3px] transition-all duration-300 transform -translate-x-1/2 -translate-y-1/2 active:scale-95 ${ 
                          rightHandSwelling.includes(joint.id) 
                            ? 'bg-[#ef4444] border-[#dc2626] shadow-lg' 
                            : 'bg-white border-[#6b7280] hover:border-[#374151] hover:shadow-md'
                        }`}
                        style={{ 
                          left: `${joint.x}%`, 
                          top: `${joint.y}%`,
                          // MOBILE OPTIMIZATION: Slightly larger touch targets while preserving positioning
                          width: 'clamp(36px, 9vw, 40px)',
                          height: 'clamp(36px, 9vw, 40px)'
                        }}
                        onClick={() => toggleRightSwelling(joint.id)}
                      />
                    ))}
                  </div>
                </div>
              )
              
            case 'photo_instructions':
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  <div className="text-center mb-8 sm:mb-12">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      Photo and Video Guide
                    </h2>
                    <p
                      className="font-bold text-black leading-fluid-lg mt-4 text-fluid-lg"
                    >
                      We'll take photos and videos of your hands in different positions.
                    </p>
                  </div>

                  {/* Streamlined 5-step instructions - SENIOR-FRIENDLY DESIGN */}
                  <div className="w-full space-y-5 mb-8">
                    {[
                      'Use good lighting and a plain background (near window with white wall or table works well)',
                      'If comfortable, please remove jewelry from your hands: rings, watches, and bracelets. This helps us see your hand joints clearly',
                      'Make sure your entire hand is visible in each photo and video',
                      'Position your hand exactly as shown in each example',
                      'Your device may ask for camera permission on the first photo. This is normal and secure - please tap "Allow" to continue with your assessment'
                    ].map((instruction, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div
                          className="rounded-full bg-brand-green flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{
                            // MOBILE OPTIMIZATION: Better touch-friendly sizing
                            width: 'clamp(24px, 6vw, 28px)',
                            height: 'clamp(24px, 6vw, 28px)'
                          }}
                        >
                          <span
                            className="text-white font-bold text-fluid-sm"
                          >
                            {index + 1}
                          </span>
                        </div>
                        <p
                          className="font-normal text-black leading-fluid-base text-fluid-base"
                        >
                          {instruction}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Privacy reassurance - MOBILE OPTIMIZATION: Better responsive text */}
                  <div className="w-full max-w-[320px] text-center mx-auto">
                    <p
                      className="font-normal text-gray-600 leading-fluid-sm text-fluid-sm"
                    >
                      Your photos and videos are private and secure.
                    </p>
                  </div>
                </div>
              )

            case 'photo':
              const getPositionInstruction = (hand: string, position: string) => {
                switch (position) {
                  case 'palm_down_spread':
                    return `Place your ${hand} hand palm down with fingers spread apart like this:`
                  case 'palm_down_fist':
                    return `Make a fist with your ${hand} hand, palm facing down like this:`
                  case 'palm_up_spread':
                    return `Place your ${hand} hand palm up with fingers spread apart like this:`
                  case 'palm_up_fist':
                    return `Make a fist with your ${hand} hand, palm facing up like this:`
                  default:
                    return `Position your ${hand} hand like this:`
                }
              }

              const getSampleImageUrl = (hand: string, position: string) => {
                return `/images/${hand}-${position}.webp`
              }

              const mediaKey = `${stepType}_${currentStepConfig.hand}_${currentStepConfig.position}`
              const photoSaved = mediaSaved[mediaKey]

              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-4">
                  <div className="text-center mb-4">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      {currentStepConfig.hand && currentStepConfig.hand.charAt(0).toUpperCase() + currentStepConfig.hand.slice(1)} Hand Photo
                    </h2>
                  </div>

                  {/* Instruction text with sample image - MOBILE OPTIMIZATION: Reduced spacing */}
                  <div className="w-full mb-4 text-center">
                    <p
                      className="font-bold text-black leading-fluid-md mb-4 text-fluid-md"
                    >
                      {getPositionInstruction(currentStepConfig.hand || 'left', currentStepConfig.position || 'palm_down_spread')}
                    </p>

                    {/* Sample image - PRESERVED IMAGE PATHS with reduced spacing */}
                    <div className="bg-white rounded-lg border border-border-light p-2 mb-4">
                      <div className="flex justify-center">
                        <img
                          src={getSampleImageUrl(currentStepConfig.hand || 'left', currentStepConfig.position || 'palm_down_spread')}
                          alt={`Sample ${currentStepConfig.hand} hand ${currentStepConfig.position}`}
                          className="rounded-lg border border-border-light w-full object-cover"
                          style={{
                            maxWidth: 'clamp(160px, 50vw, 200px)',
                            maxHeight: '25vh'
                          }}
                        />
                      </div>
                    </div>

                    {/* Photo Capture Component Integration - PRESERVED FUNCTIONALITY */}
                    <div className="w-full">
                      <PhotoCapture
                        key={`photo-${currentStepConfig.hand}-${currentStepConfig.position}`}
                        submissionId={submissionId || 'mobile-test-demo'}
                        writeToken={writeToken}
                        side={currentStepConfig.hand as 'left' | 'right'}
                        position={currentStepConfig.position}
                        existingMedia={mediaSaved[`photo_${currentStepConfig.hand}_${currentStepConfig.position}`]}
                        accountHash={imagesAccountHash}
                        onPhotoSaved={(cloudflareId) => {
                          const currentMediaKey = `photo_${currentStepConfig.hand}_${currentStepConfig.position}`
                          setMediaSaved(prev => ({
                            ...prev,
                            [currentMediaKey]: cloudflareId || 'true'
                          }))
                        }}
                      />
                    </div>
                  </div>
                </div>
              )

            case 'video':
              const getVideoInstruction = (hand: string, position: string) => {
                switch (position) {
                  case 'palm_down_movement':
                    return `Record a video of your ${hand} hand, palm down, making a fist and then spreading out your fingers, 2 times.`
                  case 'palm_up_movement':
                    return `Record a video of your ${hand} hand, palm up, making a fist and then spreading out your fingers, 2 times.`
                  default:
                    return `Record your ${hand} hand movement as shown`
                }
              }

              const getVideoSampleUrl = (hand: string, position: string) => {
                switch (position) {
                  case 'palm_down_movement':
                    return hand === 'left' ? '/images/L1.webp' : '/images/R1.webp'
                  case 'palm_up_movement':
                    return hand === 'left' ? '/images/L2.webp' : '/images/R2.webp'
                  default:
                    return '/images/L1.webp'
                }
              }

              const videoMediaKey = `${stepType}_${currentStepConfig.hand}_${currentStepConfig.position}`
              const videoSaved = mediaSaved[videoMediaKey]

              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-4">
                  <div className="text-center mb-4">
                    <h2
                      className="font-bold text-black text-fluid-xl"
                    >
                      {currentStepConfig.hand && currentStepConfig.hand.charAt(0).toUpperCase() + currentStepConfig.hand.slice(1)} Hand Video
                    </h2>
                  </div>

                  {/* Instruction text with animated sample - MOBILE OPTIMIZATION: Reduced spacing */}
                  <div className="w-full mb-4 text-center">
                    <p
                      className="font-bold text-black leading-fluid-base mb-4 text-fluid-base"
                    >
                      {getVideoInstruction(currentStepConfig.hand || 'left', currentStepConfig.position || 'palm_down_movement')}
                    </p>

                    {/* Sample animated placeholder - PRESERVED FUNCTIONALITY with reduced spacing */}
                    <div className="bg-white rounded-lg border border-border-light p-3 mb-4">
                      <p
                        className="font-semibold text-gray-500 mb-2 text-center text-fluid-xs"
                      >
                        SAMPLE VIDEO:
                      </p>
                      <div className="flex justify-center">
                        <div className="relative">
                          <img
                            src={getVideoSampleUrl(currentStepConfig.hand || 'left', currentStepConfig.position || 'palm_down_movement')}
                            alt={`Sample ${currentStepConfig.hand} hand ${currentStepConfig.position}`}
                            className="rounded-lg border border-border-light object-contain"
                            style={{
                              width: 'clamp(120px, 35vw, 160px)',
                              aspectRatio: '3/4',
                              maxHeight: '20vh'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Video Capture Component Integration - PRESERVED FUNCTIONALITY */}
                    <div className="w-full">
                      <VideoCapture
                        key={`video-${currentStepConfig.hand}-${currentStepConfig.position}`}
                        submissionId={submissionId || 'mobile-test-demo'}
                        writeToken={writeToken}
                        side={currentStepConfig.hand as 'left' | 'right'}
                        position={currentStepConfig.position}
                        existingMedia={mediaSaved[`video_${currentStepConfig.hand}_${currentStepConfig.position}`]}
                        onSaved={(uid) => {
                          const currentVideoMediaKey = `video_${currentStepConfig.hand}_${currentStepConfig.position}`
                          setMediaSaved(prev => ({
                            ...prev,
                            [currentVideoMediaKey]: uid || true
                          }))
                        }}
                      />
                    </div>
                  </div>
                </div>
              )

            case 'thank_you':
              return (
                <div className="w-full max-w-[340px] mx-auto px-4 py-8 min-h-full flex flex-col justify-center">
                  
                  {/* Thank you content - MOBILE OPTIMIZATION: Better responsive text */}
                  <div className="w-full text-center mb-12">
                    <div 
                      className="mx-auto bg-brand-green rounded-full flex items-center justify-center mb-6"
                      style={{
                        width: 'clamp(56px, 15vw, 70px)',
                        height: 'clamp(56px, 15vw, 70px)'
                      }}
                    >
                      <svg 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          width: 'clamp(28px, 8vw, 36px)',
                          height: 'clamp(28px, 8vw, 36px)'
                        }}
                      >
                        <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    
                    <p
                      className="font-bold text-black mb-gr-3 leading-fluid-xl text-fluid-2xl"
                    >
                      Thank you!
                    </p>
                    
                    <p
                      className="font-normal text-black mb-gr-4 leading-fluid-base text-fluid-base"
                    >
                      Your information has been submitted successfully.
                    </p>
                  </div>
                </div>
              )

            default:
              return <div className="text-center p-8">Step {step}: {stepType} - Coming soon...</div>
          }
        })()}
      </div>

      {/* Universal Navigation Bar - All Steps except Thank You */}
      {step !== TOTAL_STEPS && (
        <NavigationBar
          step={step}
          totalSteps={TOTAL_STEPS}
          canProceed={canProceed}
          onBack={prev}
          onNext={next}
          isFirstStep={step === 1}
          isLastStep={step === TOTAL_STEPS}
        />
      )}
    </div>
  )
}
